/**
 * The chat files a pending note action will save (#16111) — the client-safe
 * half.
 *
 * The confirmation dialog lists them by name, says which the note won't
 * take (a type it doesn't accept, or past its cap) and lets the person
 * remove any before confirming. Removing one edits the pending action's
 * `input.attachmentIds` as well as its `files`, because the confirm posts
 * `input` and the server copies exactly that — and it frees its slot, so a
 * file that was past the cap may now fit.
 *
 * Client-safe: no Prisma, no `server-only`.
 */

import {
  type NoteChatFileStatus,
  NOTE_CHAT_FILES_MAX,
  noteChatFileStatuses,
} from '@/lib/notes/note-chat-files'

import type { WorkspacePendingAction, WorkspacePendingFile } from './types'

/** The note capability input field that names chat files. */
export const NOTE_FILE_INPUT_KEY = 'attachmentIds'

/** Each pending file with what will happen to it, in order. */
export function pendingFileStatuses(
  pending: Pick<WorkspacePendingAction, 'files' | 'fileRoom'>,
): Array<{ file: WorkspacePendingFile; status: NoteChatFileStatus }> {
  const files = pending.files ?? []
  const statuses = noteChatFileStatuses(
    files,
    pending.fileRoom ?? NOTE_CHAT_FILES_MAX,
  )
  return files.map((file, index) => ({ file, status: statuses[index] }))
}

/** `pending` without one file, in what it shows and what it posts. */
export function withoutPendingFile(
  pending: WorkspacePendingAction,
  fileId: string,
): WorkspacePendingAction {
  const current = pending.input[NOTE_FILE_INPUT_KEY]
  const remaining = Array.isArray(current)
    ? current.filter(id => id !== fileId)
    : []
  const { [NOTE_FILE_INPUT_KEY]: _removed, ...rest } = pending.input
  return {
    ...pending,
    input:
      remaining.length > 0
        ? { ...rest, [NOTE_FILE_INPUT_KEY]: remaining }
        : rest,
    files: (pending.files ?? []).filter(file => file.id !== fileId),
  }
}
